import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { AiOutlineClose, AiOutlineArrowLeft, AiOutlineSearch, AiOutlineFilter, AiOutlineStar, AiFillStar } from 'react-icons/ai';
import { MdOutlineCardGiftcard, MdOutlineHelpOutline, MdOutlineLocationOn, MdOutlineAddPhotoAlternate } from 'react-icons/md';
import { FiChevronRight, FiCheck } from 'react-icons/fi';
import { AuthContext } from '../../context/AuthContext';
import { fetchOrders, updateOrderStatus, Order } from '../../store/slices/orderSlice';
import { createReview } from '../../store/slices/reviewSlice';
import { RootState, AppDispatch } from '../../store';
import { TRANSPARENT_PLACEHOLDER } from '../../utils/constants';

interface OrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const OrdersSkeleton = () => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {Array(3).fill(0).map((_, i) => (
        <div key={i} className="order-skeleton-card">
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
            <div className="order-skeleton-img shimmer-effect" />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div className="order-skeleton-line shimmer-effect" style={{ width: '60%', height: '14px' }} />
              <div className="order-skeleton-line shimmer-effect" style={{ width: '40%', height: '12px' }} />
              <div className="order-skeleton-line shimmer-effect" style={{ width: '30%', height: '10px' }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const OrdersModal: React.FC<OrdersModalProps> = ({ isOpen, onClose }) => {
  const { isAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { orders: storeOrders, loading } = useSelector((state: RootState) => state.order);
  const reduxUser = useSelector((state: RootState) => state.auth.user);

  const [orders, setOrders] = useState<Order[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Star Rating & Feedback State
  const [selectedRating, setSelectedRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [showFeedbackInput, setShowFeedbackInput] = useState<boolean>(false);
  const [reviewComment, setReviewComment] = useState<string>('');
  const [submittedRating, setSubmittedRating] = useState<boolean>(false);
  const [submittingReview, setSubmittingReview] = useState<boolean>(false);
  const [reviewImages, setReviewImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      if (isAuth) {
        setIsFetching(true);
        dispatch(fetchOrders({}))
          .then(() => setIsFetching(false))
          .catch(() => setIsFetching(false));
      } else {
        setOrders([]);
      }
    } else {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
      setSelectedOrder(null);
      setSelectedRating(0);
      setHoverRating(0);
      setShowFeedbackInput(false);
      setReviewComment('');
      setSubmittedRating(false);
      setReviewImages([]);
      setImagePreviews([]);
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [isOpen, isAuth, dispatch]);

  useEffect(() => {
    if (isAuth && storeOrders) {
      const userOrders = storeOrders.filter((order: any) => {
        const loggedInUserId = reduxUser?._id || reduxUser?.id;
        if (!loggedInUserId) return true;
        if (!order.user) return true;
        const orderUserId = typeof order.user === 'object' ? (order.user._id || order.user.id) : order.user;
        return String(orderUserId) === String(loggedInUserId);
      });
      setOrders(userOrders);

      if (selectedOrder) {
        const updated = userOrders.find(o => o._id === selectedOrder._id);
        if (updated) setSelectedOrder(updated);
      }
    } else {
      setOrders([]);
    }
  }, [storeOrders, isAuth, reduxUser]);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const matchesSearch =
        searchQuery === '' ||
        order.order_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.items?.some(item =>
          (item.name || item.product_name || '').toLowerCase().includes(searchQuery.toLowerCase())
        );

      const status = (order.status || 'pending').toLowerCase();
      let matchesFilter = true;

      if (statusFilter === 'active') {
        matchesFilter = ['pending', 'ready', 'confirmed', 'hold', 'shipped', 'out_for_delivery'].includes(status);
      } else if (statusFilter === 'delivered') {
        matchesFilter = status === 'delivered';
      } else if (statusFilter === 'cancelled') {
        matchesFilter = status === 'cancelled';
      }

      return matchesSearch && matchesFilter;
    });
  }, [orders, searchQuery, statusFilter]);

  const handleCancelOrder = async (orderId: string) => {
    if (window.confirm("Are you sure you want to cancel this order?")) {
      setCancelling(true);
      try {
        await dispatch(updateOrderStatus({ id: orderId, status: 'cancelled' })).unwrap();
        alert("Order has been cancelled successfully.");
      } catch (err: any) {
        alert(err || "Failed to cancel order.");
      } finally {
        setCancelling(false);
      }
    }
  };

  const getTimelineSteps = (order: Order) => {
    const status = (order.status || 'pending').toLowerCase();
    const history = (order as any).status_history || [];

    const formatDateTime = (dateStr: string) => {
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        return '';
      }
    };

    const findTimestamp = (stepName: string) => {
      const entry = history.find(
        (h: any) => h.status?.toLowerCase() === stepName.toLowerCase()
      );
      if (entry?.changedAt) {
        return formatDateTime(entry.changedAt);
      }
      return null;
    };

    const createdTime = formatDateTime(order.createdAt);

    const isPlaced = true;
    const isConfirmed = ['ready', 'confirmed', 'hold', 'shipped', 'out_for_delivery', 'delivered'].includes(status);
    const isShipped = ['shipped', 'out_for_delivery', 'delivered'].includes(status);
    const isOutForDelivery = ['out_for_delivery', 'delivered'].includes(status);
    const isDelivered = status === 'delivered';

    return [
      {
        id: 'placed',
        title: 'Order Placed',
        subtitle: 'Your order has been placed.',
        time: findTimestamp('pending') || createdTime,
        completed: isPlaced,
      },
      {
        id: 'confirmed',
        title: 'Order Confirmed',
        subtitle: 'Your order has been confirmed.',
        time: findTimestamp('ready') || findTimestamp('confirmed') || (isConfirmed ? createdTime : null),
        completed: isConfirmed,
      },
      {
        id: 'shipped',
        title: 'Shipped',
        subtitle: 'Your order has been shipped.',
        time: findTimestamp('shipped'),
        completed: isShipped,
      },
      {
        id: 'out_for_delivery',
        title: 'Out for Delivery',
        subtitle: 'Your order is out for delivery.',
        time: findTimestamp('out_for_delivery'),
        completed: isOutForDelivery,
      },
      {
        id: 'delivered',
        title: 'Delivered',
        subtitle: 'Your order has been delivered.',
        time: findTimestamp('delivered'),
        completed: isDelivered,
      },
    ];
  };

  const getOrderStatusBanner = (status: string, createdAt?: string) => {
    const st = (status || 'pending').toLowerCase();
    if (st === 'delivered') return 'Delivered on ' + (createdAt ? new Date(createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Aug 18, 2026');
    if (st === 'cancelled') return 'Order Cancelled';
    return 'Delivery Expected within 4-5 working days';
  };

  const getOrderStatusSubtitle = (status: string) => {
    const st = (status || 'pending').toLowerCase();
    if (st === 'delivered') return 'Your Order has been Delivered.';
    if (st === 'cancelled') return 'Your Order has been Cancelled.';
    if (st === 'shipped') return 'Your Order has been Shipped.';
    if (st === 'out_for_delivery') return 'Your Order is Out for Delivery.';
    if (st === 'ready' || st === 'confirmed') return 'Your Order has been Confirmed.';
    return 'Your Order has been Accepted';
  };

  if (!isOpen) return null;

  return (
    <div className="orders-modal-overlay" onClick={onClose}>
      <style>
        {`
        .orders-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          justify-content: flex-end;
          background-color: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          animation: fadeInOverlay 0.3s ease;
        }

        @keyframes fadeInOverlay {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }

        .orders-drawer {
          animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          width: 30vw !important;
          min-width: 420px !important;
          max-width: 550px !important;
          background-color: #f8fafc;
          height: 100%;
          display: flex;
          flex-direction: column;
          box-shadow: -15px 0 45px rgba(15, 23, 42, 0.12);
          border-top-left-radius: 20px;
          border-bottom-left-radius: 20px;
          overflow: hidden;
          font-family: inherit;
        }

        .orders-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid #e2e8f0;
          background-color: #ffffff;
        }

        .orders-header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .orders-header-title {
          font-size: 16px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
          letter-spacing: -0.2px;
        }

        .header-icon-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 6px;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .header-icon-btn:hover {
          background-color: #f1f5f9;
          color: #0f172a;
        }

        .search-filter-bar {
          display: flex;
          gap: 10px;
          padding: 14px 20px;
          background-color: #ffffff;
          border-bottom: 1px solid #f1f5f9;
          position: relative;
          z-index: 50;
        }

        .search-input-wrapper {
          flex: 1;
          display: flex;
          align-items: center;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 0 12px;
          gap: 8px;
        }

        .search-input-wrapper input {
          width: 100%;
          border: none;
          background: transparent;
          padding: 10px 0;
          font-size: 13px;
          outline: none;
          color: #0f172a;
        }

        .filter-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border: 1px solid #8b5cf6;
          color: #7c3aed;
          background: #ffffff;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .filter-btn:hover {
          background-color: #f5f3ff;
        }

        .filter-dropdown-menu {
          position: absolute;
          top: 54px;
          right: 20px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.12);
          z-index: 100;
          overflow: hidden;
          min-width: 150px;
        }

        .filter-option {
          padding: 10px 16px;
          font-size: 13px;
          color: #334155;
          cursor: pointer;
          transition: background 0.15s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .filter-option:hover, .filter-option.active {
          background: #f5f3ff;
          color: #7c3aed;
          font-weight: 600;
        }

        .orders-content {
          padding: 16px 20px;
          overflow-y: auto;
          flex: 1;
        }

        /* Order List Card Style (Bhutan Mart Theme) */
        .bm-order-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          margin-bottom: 16px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.02);
          transition: all 0.2s ease;
        }

        .bm-order-card:hover {
          border-color: #cbd5e1;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }

        .bm-order-main-body {
          padding: 16px;
          display: flex;
          gap: 14px;
          align-items: flex-start;
          cursor: pointer;
        }

        .bm-prod-thumb {
          width: 64px;
          height: 64px;
          border-radius: 12px;
          object-fit: cover;
          background-color: #f8fafc;
          border: 1px solid #f1f5f9;
          flex-shrink: 0;
        }

        .bm-order-info {
          flex: 1;
          min-width: 0;
        }

        .bm-expected-delivery {
          font-size: 13.5px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 4px;
          letter-spacing: -0.2px;
        }

        .bm-status-sub {
          font-size: 12px;
          color: #64748b;
          margin-bottom: 6px;
          font-weight: 500;
        }

        .bm-item-preview {
          font-size: 12px;
          color: #94a3b8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .bm-feedback-bar {
          padding: 12px 16px;
          border-top: 1px solid #f1f5f9;
          background: #ffffff;
        }

        .bm-feedback-btn {
          font-size: 11px;
          font-weight: 800;
          color: #7c3aed;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
        }

        .bm-feedback-btn:hover {
          text-decoration: underline;
        }

        /* Order Details View (Bhutan Mart Theme) */
        .delivery-address-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 16px;
          margin-top: 16px;
        }

        /* Product Rating Feedback Card */
        .product-feedback-card {
          background: #fcf7ff;
          border: 1px solid #f3e8ff;
          border-radius: 18px;
          padding: 20px 16px;
          margin-top: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          box-shadow: 0 4px 15px rgba(139, 92, 246, 0.05);
        }

        .feedback-title {
          font-size: 16px;
          font-weight: 700;
          color: #1e293b;
          text-align: center;
          margin: 0;
        }

        .feedback-stars-row {
          display: flex;
          align-items: center;
          justify-content: space-around;
          width: 100%;
          padding: 0 4px;
        }

        .star-item-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .star-item-col:hover {
          transform: scale(1.1);
        }

        .star-label {
          font-size: 11px;
          font-weight: 600;
          color: #64748b;
          transition: color 0.2s ease;
        }

        .star-item-col.active .star-label {
          color: #7c3aed;
          font-weight: 700;
        }

        .feedback-btn {
          width: 100%;
          padding: 12px 16px;
          background: #8b5cf6;
          color: #ffffff;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: background-color 0.2s ease, transform 0.1s ease;
          box-shadow: 0 4px 12px rgba(139, 92, 246, 0.22);
        }

        .feedback-btn:hover {
          background-color: #7c3aed;
        }

        .feedback-btn:active {
          transform: scale(0.98);
        }

        .feedback-textarea {
          width: 100%;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 10px 14px;
          font-size: 13px;
          outline: none;
          background: #ffffff;
          resize: none;
          color: #0f172a;
          font-family: inherit;
        }

        .review-upload-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 10px 14px;
          background: #ffffff;
          border: 1px dashed #a855f7;
          border-radius: 12px;
          color: #7c3aed;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .review-upload-btn:hover {
          background: #f5f3ff;
          border-color: #7c3aed;
        }

        .review-image-previews {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          width: 100%;
        }

        .preview-thumb-wrapper {
          position: relative;
          width: 56px;
          height: 56px;
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
        }

        .preview-thumb-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .remove-thumb-btn {
          position: absolute;
          top: 2px;
          right: 2px;
          background: rgba(15, 23, 42, 0.75);
          color: #ffffff;
          border: none;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          font-size: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .details-top-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 16px;
        }

        .details-order-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .details-order-id {
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
        }

        .details-prod-row {
          display: flex;
          gap: 14px;
          align-items: center;
        }

        .details-prod-img {
          width: 60px;
          height: 60px;
          border-radius: 10px;
          object-fit: cover;
          border: 1px solid #f1f5f9;
          background: #f8fafc;
        }

        .details-prod-title {
          font-size: 13.5px;
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 4px;
        }

        .details-price-row {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
        }

        .price-current {
          font-weight: 700;
          color: #0f172a;
        }

        .price-mrp {
          color: #94a3b8;
          text-decoration: line-through;
          font-size: 12px;
        }

        .price-off {
          color: #16a34a;
          font-weight: 700;
          font-size: 11px;
        }

        .details-meta-line {
          font-size: 11.5px;
          color: #64748b;
          margin-top: 4px;
        }

        .returns-badge {
          display: inline-block;
          margin-top: 8px;
          background: #f1f5f9;
          color: #475569;
          font-size: 10px;
          font-weight: 600;
          padding: 3px 8px;
          border-radius: 6px;
        }

        /* Stepper Timeline Card */
        .timeline-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 20px 18px;
          margin-bottom: 16px;
        }

        .stepper-item {
          display: flex;
          position: relative;
          padding-bottom: 24px;
        }

        .stepper-item:last-child {
          padding-bottom: 0;
        }

        .stepper-line {
          position: absolute;
          left: 11px;
          top: 24px;
          bottom: 0;
          width: 2px;
          background-color: #e2e8f0;
        }

        .stepper-item.completed .stepper-line {
          background-color: #16a34a;
        }

        .stepper-icon-col {
          width: 24px;
          margin-right: 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          z-index: 1;
        }

        .stepper-circle {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #cbd5e1;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          font-size: 12px;
          transition: all 0.2s ease;
        }

        .stepper-item.completed .stepper-circle {
          background: #16a34a;
        }

        .stepper-content {
          flex: 1;
        }

        .stepper-title {
          font-size: 13.5px;
          font-weight: 700;
          color: #64748b;
          margin-bottom: 2px;
        }

        .stepper-item.completed .stepper-title {
          color: #0f172a;
        }

        .stepper-sub {
          font-size: 12px;
          color: #64748b;
          margin-bottom: 2px;
        }

        .stepper-time {
          font-size: 11px;
          color: #94a3b8;
        }

        /* Action Buttons & Info Boxes */
        .cancel-btn {
          width: 100%;
          padding: 12px;
          border: 1px solid #fca5a5;
          background: #ffffff;
          color: #ef4444;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 16px;
        }

        .cancel-btn:hover {
          background-color: #fef2f2;
        }

        .info-exchange-box {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 14px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 12px;
          font-weight: 600;
          color: #334155;
          margin-bottom: 16px;
        }

        .info-exchange-btn {
          color: #7c3aed;
          font-weight: 800;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 11px;
        }

        .delivery-address-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 16px;
        }

        .address-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .address-header-title {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #0284c7;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.5px;
        }

        .address-type-badge {
          background: #e2e8f0;
          color: #475569;
          font-size: 9.5px;
          font-weight: 800;
          padding: 2px 6px;
          border-radius: 4px;
          text-transform: uppercase;
        }

        .address-recipient {
          font-size: 13px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 4px;
        }

        .address-full {
          font-size: 12px;
          color: #64748b;
          line-height: 1.4;
          margin-bottom: 4px;
        }

        .address-phone {
          font-size: 12px;
          color: #64748b;
          font-weight: 500;
        }

        .orders-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #64748b;
          padding: 40px 20px;
          text-align: center;
        }

        .orders-empty-icon-wrapper {
          width: 80px;
          height: 80px;
          background-color: #f5f3ff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          color: #7c3aed;
        }

        .orders-empty h3 {
          font-size: 18px;
          font-weight: 800;
          color: #0f172a;
          margin: 0 0 6px 0;
        }

        .orders-empty p {
          font-size: 13px;
          color: #64748b;
          margin: 0 0 20px 0;
        }

        .orders-empty-cta {
          padding: 10px 24px;
          background-color: #7c3aed;
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          text-decoration: none;
        }
        `}
      </style>

      <div className="orders-drawer" onClick={e => e.stopPropagation()}>
        {/* Drawer Header */}
        <div className="orders-header">
          <div className="orders-header-left">
            {selectedOrder ? (
              <button
                onClick={() => setSelectedOrder(null)}
                className="header-icon-btn"
                aria-label="Back to orders list"
              >
                <AiOutlineArrowLeft size={18} />
              </button>
            ) : (
              <MdOutlineCardGiftcard size={22} color="#7c3aed" />
            )}
            <h2 className="orders-header-title">
              {selectedOrder ? 'ORDER DETAILS' : 'Orders'}
            </h2>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {selectedOrder && (
              <button className="header-icon-btn" style={{ color: '#7c3aed', gap: '4px', fontSize: '12px', fontWeight: 700 }}>
                <MdOutlineHelpOutline size={16} /> HELP
              </button>
            )}
            <button onClick={onClose} className="header-icon-btn" aria-label="Close modal">
              <AiOutlineClose size={18} />
            </button>
          </div>
        </div>

        {/* VIEW 1: ORDERS LIST */}
        {!selectedOrder ? (
          <>
            {/* Search Bar */}
            <div className="search-filter-bar">
              <div className="search-input-wrapper">
                <AiOutlineSearch size={16} color="#94a3b8" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Orders List Body */}
            <div className="orders-content">
              {!isAuth ? (
                <div className="orders-empty">
                  <div className="orders-empty-icon-wrapper">
                    <MdOutlineCardGiftcard size={32} />
                  </div>
                  <h3>Login to View Orders</h3>
                  <p>Please log in to see your order history and live tracking.</p>
                  <Link to="/login" onClick={onClose} className="orders-empty-cta">
                    Login
                  </Link>
                </div>
              ) : (loading || isFetching) ? (
                <OrdersSkeleton />
              ) : filteredOrders.length > 0 ? (
                <div>
                  {filteredOrders.map(order => {
                    const firstItem = order.items && order.items.length > 0 ? order.items[0] : null;
                    const imageUrl = firstItem?.image ||
                      (firstItem?.images && firstItem.images.length > 0
                        ? (typeof firstItem.images[0] === 'string' ? firstItem.images[0] : firstItem.images[0].url)
                        : TRANSPARENT_PLACEHOLDER);

                    const title = firstItem?.name || firstItem?.product_name || 'Order Item';
                    const qty = firstItem?.quantity || 1;

                    return (
                      <div key={order._id} className="bm-order-card">
                        <div
                          className="bm-order-main-body"
                          onClick={() => setSelectedOrder(order)}
                        >
                          <img
                            src={imageUrl}
                            alt={title}
                            className="bm-prod-thumb"
                            onError={e => { (e.target as HTMLImageElement).src = TRANSPARENT_PLACEHOLDER; }}
                          />
                          <div className="bm-order-info">
                            <div className="bm-expected-delivery">
                              {title}
                            </div>
                            <div className="bm-status-sub">
                              {getOrderStatusSubtitle(order.status)}
                            </div>
                            <div className="bm-item-preview">
                              Qty: {qty} • Total: ₹{order.total_amount?.toLocaleString('en-IN')}
                            </div>
                          </div>
                          <FiChevronRight size={20} color="#94a3b8" style={{ marginTop: '4px' }} />
                        </div>

                        <div className="bm-feedback-bar">
                          <button
                            className="bm-feedback-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOrder(order);
                            }}
                          >
                            ADD FEEDBACK
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="orders-empty">
                  <div className="orders-empty-icon-wrapper">
                    <MdOutlineCardGiftcard size={32} />
                  </div>
                  <h3>No Orders Found</h3>
                  <p>We couldn't find any orders matching your search or filters.</p>
                  <button onClick={() => { onClose(); navigate('/'); }} className="orders-empty-cta">
                    Start Shopping
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          /* VIEW 2: ORDER DETAILS (WHEN CLICKED ON AN ORDER) */
          <div className="orders-content">
            {/* Top Product Summary Card */}
            <div className="details-top-card">
              <div className="details-order-header">
                <span className="details-order-id">
                  Order #{selectedOrder.order_id || selectedOrder._id?.slice(-8).toUpperCase()}
                </span>
                <FiChevronRight size={18} color="#94a3b8" />
              </div>

              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div className="details-prod-row">
                  <img
                    src={selectedOrder.items[0].image || (selectedOrder.items[0].images?.[0] ? (typeof selectedOrder.items[0].images[0] === 'string' ? selectedOrder.items[0].images[0] : selectedOrder.items[0].images[0].url) : TRANSPARENT_PLACEHOLDER)}
                    alt=""
                    className="details-prod-img"
                    onError={e => { (e.target as HTMLImageElement).src = TRANSPARENT_PLACEHOLDER; }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="details-prod-title">
                      {selectedOrder.items[0].name || selectedOrder.items[0].product_name}
                    </div>
                    <div className="details-price-row">
                      <span className="price-current">₹{selectedOrder.items[0].price?.toLocaleString('en-IN')}</span>
                      {(selectedOrder.items[0] as any).mrp && (selectedOrder.items[0] as any).mrp > selectedOrder.items[0].price && (
                        <>
                          <span className="price-mrp">₹{(selectedOrder.items[0] as any).mrp.toLocaleString('en-IN')}</span>
                          <span className="price-off">
                            ({Math.round((((selectedOrder.items[0] as any).mrp - selectedOrder.items[0].price) / (selectedOrder.items[0] as any).mrp) * 100)}% OFF)
                          </span>
                        </>
                      )}
                    </div>
                    <div className="details-meta-line">
                      piece • Qty: {selectedOrder.items[0].quantity} • {selectedOrder.payment_method || 'COD'}
                    </div>
                    <div className="returns-badge">
                      All issue easy returns
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Status Stepper Timeline Card (5 Steps) */}
            <div className="timeline-card">
              {getTimelineSteps(selectedOrder).map((step, idx, arr) => (
                <div
                  key={step.id}
                  className={`stepper-item ${step.completed ? 'completed' : ''}`}
                >
                  {idx < arr.length - 1 && <div className="stepper-line" />}
                  <div className="stepper-icon-col">
                    <div className="stepper-circle">
                      {step.completed ? <FiCheck size={12} strokeWidth={3} /> : null}
                    </div>
                  </div>
                  <div className="stepper-content">
                    <div className="stepper-title">{step.title}</div>
                    <div className="stepper-sub">{step.subtitle}</div>
                    {step.time && <div className="stepper-time">{step.time}</div>}
                  </div>
                </div>
              ))}
            </div>

            {/* Delivered Order Product Feedback / Rating Card */}
            {((selectedOrder.status || '').toLowerCase() === 'delivered') && (
              <div className="product-feedback-card">
                <h4 className="feedback-title">How was the product?</h4>
                <div className="feedback-stars-row">
                  {[
                    { rating: 1, label: 'Very Bad' },
                    { rating: 2, label: 'Bad' },
                    { rating: 3, label: 'Ok-Ok' },
                    { rating: 4, label: 'Good' },
                    { rating: 5, label: 'Very Good' },
                  ].map(opt => {
                    const activeRating = hoverRating || selectedRating;
                    const isFilled = opt.rating <= activeRating;
                    return (
                      <div
                        key={opt.rating}
                        className={`star-item-col ${isFilled ? 'active' : ''}`}
                        onMouseEnter={() => setHoverRating(opt.rating)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setSelectedRating(opt.rating)}
                      >
                        {isFilled ? (
                          <AiFillStar size={28} color="#8b5cf6" />
                        ) : (
                          <AiOutlineStar size={28} color="#cbd5e1" />
                        )}
                        <span className="star-label">{opt.label}</span>
                      </div>
                    );
                  })}
                </div>

                {showFeedbackInput && !submittedRating && (
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <textarea
                      className="feedback-textarea"
                      rows={3}
                      placeholder="Write your detailed feedback / review..."
                      value={reviewComment}
                      onChange={e => setReviewComment(e.target.value)}
                    />

                    <input
                      type="file"
                      id="review-image-input"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={e => {
                        if (e.target.files) {
                          const filesArray = Array.from(e.target.files);
                          setReviewImages(prev => [...prev, ...filesArray]);
                          const previews = filesArray.map(file => URL.createObjectURL(file));
                          setImagePreviews(prev => [...prev, ...previews]);
                        }
                      }}
                    />

                    <label htmlFor="review-image-input" className="review-upload-btn">
                      <MdOutlineAddPhotoAlternate size={20} />
                      <span>{reviewImages.length > 0 ? `Add More Photos (${reviewImages.length} attached)` : 'Attach Product Photos'}</span>
                    </label>

                    {imagePreviews.length > 0 && (
                      <div className="review-image-previews">
                        {imagePreviews.map((src, idx) => (
                          <div key={idx} className="preview-thumb-wrapper">
                            <img src={src} alt="Review upload preview" className="preview-thumb-img" />
                            <button
                              type="button"
                              className="remove-thumb-btn"
                              onClick={() => {
                                setReviewImages(prev => prev.filter((_, i) => i !== idx));
                                setImagePreviews(prev => prev.filter((_, i) => i !== idx));
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <button
                  className="feedback-btn"
                  disabled={submittingReview}
                  onClick={async () => {
                    if (submittedRating || submittingReview) return;
                    if (!showFeedbackInput && !selectedRating) {
                      setShowFeedbackInput(true);
                      return;
                    }
                    if (!showFeedbackInput) {
                      setShowFeedbackInput(true);
                    } else {
                      const firstItem = selectedOrder.items && selectedOrder.items.length > 0 ? selectedOrder.items[0] : null;
                      const productId = firstItem?.product_id || (firstItem as any)?._id || (selectedOrder as any).product_id;
                      const userId = reduxUser?._id || reduxUser?.id;
                      const fullName = (reduxUser?.first_name || reduxUser?.last_name)
                        ? `${reduxUser.first_name || ''} ${reduxUser.last_name || ''}`.trim()
                        : (selectedOrder.address as any)?.name || selectedOrder.customer_name || 'Customer';

                      setSubmittingReview(true);
                      try {
                        if (reviewImages.length > 0) {
                          const formData = new FormData();
                          if (productId) formData.append('product_id', String(productId));
                          if (userId) formData.append('user_id', String(userId));
                          formData.append('rating', String(selectedRating || 5));
                          if (reviewComment.trim()) formData.append('comment', reviewComment.trim());
                          formData.append('userInfo', JSON.stringify({ fullname: fullName, email: reduxUser?.email, phone: reduxUser?.phone_number }));
                          reviewImages.forEach(file => {
                            formData.append('images', file);
                          });
                          await dispatch(createReview(formData)).unwrap();
                        } else {
                          await dispatch(createReview({
                            product_id: productId ? String(productId) : undefined,
                            user_id: userId ? String(userId) : undefined,
                            rating: selectedRating || 5,
                            comment: reviewComment.trim() || 'Good quality product!',
                            userInfo: { fullname: fullName, email: reduxUser?.email, phone: reduxUser?.phone_number },
                          })).unwrap();
                        }
                        setSubmittedRating(true);
                      } catch (err: any) {
                        console.error("Review submission:", err);
                        setSubmittedRating(true);
                      } finally {
                        setSubmittingReview(false);
                      }
                    }
                  }}
                >
                  {submittedRating
                    ? '✓ Feedback Submitted! Thank you'
                    : submittingReview
                    ? 'Submitting Review...'
                    : showFeedbackInput
                    ? 'Submit Review'
                    : 'Add Detailed Feedback / Review'}
                </button>
              </div>
            )}

            {/* Cancel Order Button */}
            {['pending', 'ready', 'confirmed', 'hold'].includes((selectedOrder.status || '').toLowerCase()) && (
              <button
                className="cancel-btn"
                disabled={cancelling}
                onClick={() => handleCancelOrder(selectedOrder._id)}
              >
                {cancelling ? 'Cancelling Order...' : 'Cancel Order'}
              </button>
            )}

            {/* Exchange Policy Box */}
            <div className="info-exchange-box">
              <span>No Return - Exchange available</span>
              <button className="info-exchange-btn">KNOW MORE</button>
            </div>

            {/* Delivery Address Card */}
            {(selectedOrder.address || selectedOrder.shipping_address) && (
              <div className="delivery-address-card">
                <div className="address-card-header">
                  <div className="address-header-title">
                    <MdOutlineLocationOn size={16} /> DELIVERY ADDRESS
                  </div>
                  <span className="address-type-badge">
                    {selectedOrder.address?.type || 'WORK'}
                  </span>
                </div>
                <div className="address-recipient">
                  {selectedOrder.address?.name || selectedOrder.customer_name || 'Customer'}
                </div>
                <div className="address-full">
                  {selectedOrder.shipping_address ||
                    (selectedOrder.address
                      ? `${selectedOrder.address.address}, ${selectedOrder.address.locality ? selectedOrder.address.locality + ', ' : ''}${selectedOrder.address.city}, ${selectedOrder.address.state} - ${selectedOrder.address.pincode}`
                      : 'Address not available')}
                </div>
                <div className="address-phone">
                  Phone: {selectedOrder.shipping_phone || selectedOrder.address?.shipping_phone || 'Not available'}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersModal;
